/**
 * Extracts the order of magnitude and scaled value (1-10 range) from a number.
 * Used to snap values to "nice" numbers (1, 2, 5, 10) for readable axis labels.
 */
const getMagnitudeAndScale = (value: number): { magnitude: number; scaledValue: number } => {
  if (value <= 0) return { magnitude: 1, scaledValue: 1 };

  // Order of magnitude (e.g., 35 → 10, 350 → 100)
  const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
  // Scale to 1-10 range for threshold comparison
  const scaledValue = value / magnitude;
  return { magnitude, scaledValue };
};

/**
 * Calculates the spacing between y-axis ticks to produce clean numbers.
 * Given a max value, returns an interval (e.g., 1, 2, 5, 10, 20, 50) that
 * results in approximately `targetTickCount` ticks on the axis.
 *
 * @example
 * calculateNiceInterval(10) // returns 2 (ticks at 0, 2, 4, 6, 8, 10)
 * calculateNiceInterval(100) // returns 20 (ticks at 0, 20, 40, 60, 80, 100)
 */
export const calculateNiceInterval = (max: number, targetTickCount = 5): number => {
  if (max <= 0) return 1;

  const roughInterval = max / targetTickCount;
  const { magnitude, scaledValue } = getMagnitudeAndScale(roughInterval);

  // Snap to nearest "nice" number (1, 2, 5, 10) at the current magnitude
  if (scaledValue <= 1) return magnitude;
  if (scaledValue <= 2) return magnitude * 2;
  if (scaledValue <= 5) return magnitude * 5;
  return magnitude * 10;
};

/**
 * Calculates the y-axis maximum value when no explicit max is provided.
 * Takes the data's max value and returns a "nice" rounded number above it,
 * providing headroom so bars don't touch the top of the chart.
 *
 * @example
 * calculateNiceMax(7) // returns 10
 * calculateNiceMax(550) // returns 600
 * calculateNiceMax(1200) // returns 1400
 */
const calculateNiceMax = (max: number): number => {
  if (max <= 0) return 10;

  const { magnitude, scaledValue } = getMagnitudeAndScale(max);

  // Determine interval based on where scaledValue falls in 1-10 range
  let niceInterval: number;
  if (scaledValue <= 1) niceInterval = magnitude / 5;
  else if (scaledValue <= 2) niceInterval = magnitude / 2;
  else if (scaledValue <= 5) niceInterval = magnitude;
  else niceInterval = magnitude * 2;

  // Calculate the last tick based on the nice interval
  const lastTick = Math.ceil(max / niceInterval) * niceInterval;

  // Add one interval to provide headroom above the highest data point
  return lastTick + niceInterval;
};

interface YAxisConfigInput {
  /** The maximum value in the data */
  maxDataValue: number;
  /** Optional explicit max value for the y-axis */
  yAxisMax?: number;
  /** Whether to allow decimal tick values */
  allowDecimals?: boolean;
}

/**
 * Calculates y-axis configuration including max value and tick positions.
 * When allowDecimals is false, generates integer-only ticks.
 */
export const calculateYAxisConfig = ({ maxDataValue, yAxisMax, allowDecimals = true }: YAxisConfigInput) => {
  if (allowDecimals) {
    return {
      yAxisMax: yAxisMax ?? calculateNiceMax(maxDataValue),
      yAxisTicks: undefined,
    };
  }

  // For integer-only ticks, calculate explicit tick positions
  const baseMax = yAxisMax ?? calculateNiceMax(maxDataValue);
  const interval = calculateNiceInterval(baseMax);
  const integerInterval = Math.max(1, Math.ceil(interval));
  const max = Math.ceil(maxDataValue / integerInterval) * integerInterval + integerInterval;

  const ticks: number[] = [];
  for (let tick = 0; tick <= max; tick += integerInterval) {
    ticks.push(tick);
  }

  return { yAxisMax: max, yAxisTicks: ticks };
};
